import React, { useEffect, useState } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { Header } from '@/components/molecules/front-tablet/header'
import { SupportQr } from '@/components/organisms/self-checkin/payment/support-qr'
import { ProgressDialog } from '@/components/organisms/self-checkin/payment/progress-dialog'
import QrReader from 'react-qr-reader'
import { SuccessDialog } from '@/components/organisms/self-checkin/payment/success-dialog'
import { ErrorDialog } from '@/components/organisms/self-checkin/payment/error-dialog'
import * as vescajs from '@/apis/vescajs'
import { FrontTabletLayout } from '@/components/layouts/front-tablet-layout'
import { useSelfCheckOutState } from '@/hooks/use-self-check-out-state'
import { reportError } from '@/components/apps/error-boundary'

export const PaymentTerminalWithQr: React.FC = () => {
  const { t, i18n } = useTranslation()
  const history = useHistory()
  const { state, doCheckOut, stateErrorHandling } = useSelfCheckOutState()
  const [isStopScan, setIsStopScan] = useState(false)
  const [vesca, setVesca] = useState<vescajs.VescaJs>()
  const [receiveTerminalStatus, setReceiveTerminalStatus] = useState<'progress' | 'success' | 'error'>()

  // note ブラウザバックなどで決済ページを復元させないために、履歴にスタックさせないようにreplaceをもちいる
  const goBack = () => history.replace({ pathname: '/self-checkout/payment' })

  const onSuccess = async (event: vescajs.OutputCompleteEvent) => {
    setReceiveTerminalStatus('success')
    try {
      await doCheckOut({ ...state, payment: { ...event.pmsResult, kind: 'credit', deviceType: vescajs.DeviceType.PaidQr } }, i18n.language)
      history.replace({ pathname: '/self-checkout/complete' })
    } catch (e) {
      reportError(e)
      alert(t('Please complete the procedure at the front desk'))
      history.push({ pathname: `/self-checkin`, search: `?hotelId=${state.hotelId}` })
    }
  }

  const onScan = (source: string | null) => {
    if (source === null || isStopScan) {
      return
    }
    setIsStopScan(true)
    vesca!.doQrPay({
      amount: state.selectReservation!.payment!.usage.totalAmount,
      tax: 0,
      qrCode: source,
    })
    setReceiveTerminalStatus('progress')
  }

  useEffect(() => {
    if (!stateErrorHandling(state, 'selectReservation')) {
      return
    }
    const vescaJs = new vescajs.VescaJs({
      hotelId: state.hotelId,
      handler: {
        onCancel: () => {
          setReceiveTerminalStatus(undefined)
          goBack()
        },
        onError: () => {
          setReceiveTerminalStatus('error')
          setTimeout(() => goBack(), 5000)
        },
        onSuccess,
      },
      paymentTarget: {
        reservationIds: state.selectReservation.selectedReservationIds,
        guestRoomAssignIds: state.selectReservation.selectedReservationAndRooms.flatMap(r => r.guestRoomAssignIds),
      },
    })
    setVesca(vescaJs)

    return () => {
      vescaJs.dispose()
    }
  }, [])

  return (
    <FrontTabletLayout state={state}>
      <Header goToPreviousPage={() => goBack()} title={t('Payment details')} />
      <div css={containerStyle}>
        <div style={{ maxWidth: 880 }}>
          <div className="operation-block">
            <div>{t('Hold the code up to the camera')}</div>
          </div>

          <div className="terminal-and-brand-block">
            <div style={{ width: '50%', marginRight: 12 }}>
              <div className="terminal-image-block">
                <img src={require('@/static/images/self-checkin-payment/terminal/image_03.png')} />
              </div>
              <SupportQr className="brand-image-block" />
            </div>
            <div className="camera-block" style={{ width: '50%', marginLeft: 12 }}>
              {/* docs: https://www.npmjs.com/package/react-qr-reader/v/2.2.1 */}
              <QrReader
                showViewFinder={false}
                onScan={onScan}
                onError={() => {}}
                facingMode="user"
                constraints={{
                  facingMode: 'user',
                  // 極力高解像度で撮影できるように
                  width: { ideal: 1920 },
                  height: { ideal: 1080 },
                }}
                resolution={1000}
                // スキャンの試行間隔を短く
                delay={200}
              />
              <div className="camera-overlay">
                <div className="operation-message">{t('Please align QR with the frame')}</div>
                <canvas className="image-frame" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {receiveTerminalStatus === 'progress' && <ProgressDialog />}
      {receiveTerminalStatus === 'success' && <SuccessDialog />}
      {receiveTerminalStatus === 'error' && <ErrorDialog />}
    </FrontTabletLayout>
  )
}

const containerStyle = css({
  backgroundColor: '#f2f2f2',
  display: 'flex',
  justifyContent: 'center',
  // add header size (=80)
  paddingTop: 32 + 80,
  paddingBottom: 32,
  height: '100vh',
  width: '100%',
  '*': { fontFamily: 'Noto ans JP', color: '#272727' },
  '.operation-block': {
    textAlign: 'center',
    'div:first-of-type': {
      fontSize: 21,
      lineHeight: 1.5,
      fontWeight: 'bold',
      letterSpacing: 3.15,
    },
  },
  '.terminal-and-brand-block': {
    marginTop: 32,
    backgroundColor: '#ffffff',
    borderRadius: 10,
    border: '1px solid #F2F2F2',
    padding: 40,
    display: 'flex',
    '.terminal-image-block': {
      img: { width: '100%' },
    },
    '.brand-image-block': {
      marginTop: 24,
    },
    '.camera-block': {
      background: '#000',
      position: 'relative',
      // QrReader
      section: { height: '100%' },
      '.camera-overlay': {
        position: 'absolute',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        width: '100%',
        textAlign: 'center',
        '.operation-message': {
          fontSize: 16,
          fontWeight: 'bold',
          color: '#ffffff',
          letterSpacing: 2.4,
        },
        '.image-frame': {
          marginTop: 24,
          aspectRatio: '1',
          width: 'calc(100% - 160px)',
          borderRadius: 2,
          border: '3px solid #fff',
        },
      },
    },
  },
})
